import {
  Actor,
  Color,
  Entity,
  KeyEvent,
  Keys,
  Rectangle,
  Tile,
  TransformComponent,
  vec,
  Vector,
} from "excalibur";
import { Building } from "../actors/building";
import { PreviewBuilding } from "../actors/previewBuilding";
import { ErrorPreviewBuilding } from "../actors/errorPreviewBuilding";
import { notEmpty } from "../common";
import { PreviewZIndex, TileHalfSize, TileSize } from "../constants";
import {
  BuildError,
  CombinableComponent,
  findBuildableOption,
} from "../ecs/combinable";
import { BaseFlow } from "../engine/baseFlow";
import { MyLevel } from "../level";
import {
  Playable,
  PlayableId,
  DynamicResources,
  GameResources,
} from "../types";
import { actorName } from "../utils/actor";
import { TileEvent, toTileEvent } from "./TileEvent";
import { ResourcePerTurnComponent } from "../ecs/resources";
import { isMobile } from "../utils/mobile";
import { TargetableComponent } from "../ecs/target";

export type PlaceBuildingResult =
  | { placed: true; pos: Vector; playableId: PlayableId; combined: boolean }
  | { placed: false };

const upgradeOverlay = new Rectangle({
  width: TileSize,
  height: TileSize,
  color: Color.Orange,
  opacity: 0.4,
});

const errorOverlay = new Rectangle({
  width: TileSize,
  height: TileSize,
  color: Color.Red,
  opacity: 0.4,
});

const confirmationOverlay = new Rectangle({
  width: TileSize,
  height: TileSize,
  color: Color.Blue,
  opacity: 0.3,
});

export class PlaceBuildingFlow extends BaseFlow<PlaceBuildingResult> {
  level: MyLevel;
  private _towerPreview: Actor | undefined;
  private _towerUpgradeMask: Actor | undefined;
  private _confirmationTile: Tile | undefined;
  private _confirmationOverlay: Actor | undefined;

  get confirmationTile(): Tile | undefined {
    return this._confirmationTile;
  }

  private calculateCombinationDelta(
    newPlayable: Playable,
    matchingNeighbors: Entity<CombinableComponent>[],
  ): DynamicResources {
    // Start with the new building's resources
    const delta: DynamicResources = { ...newPlayable.resources };

    // Subtract resources from buildings being replaced
    for (const neighbor of matchingNeighbors) {
      const resourceComponent = neighbor.get(ResourcePerTurnComponent);
      if (resourceComponent) {
        delta.ore -= resourceComponent.ore;
        delta.food -= resourceComponent.food;
        delta.fuel -= resourceComponent.fuel;
        delta.fight -= resourceComponent.fight;
        delta.money -= resourceComponent.money;
      }
    }

    return delta;
  }

  constructor(
    level: MyLevel,
    public playable: Playable,
  ) {
    super(level.engine.clock);
    this.level = level;
  }

  canPlace(x: number, y: number) {
    if (this.level.turnBasedSystem.updating) return false;

    const tile = this.level.pathmap.getTile(x, y);
    return !!(
      tile &&
      !tile.solid &&
      !tile?.data.get("ex-tiled").properties.get("nobuild") &&
      this.level.tileManager
        .at(x, y)
        .filter((e) => e.owner?.id != this._towerPreview?.id).length == 0
    );
  }

  private getNeighborsAt(pos: Vector) {
    return this.level.tileManager.getNeighborsAt(pos);
  }

  private getBuildingAtTile(x: number, y: number) {
    const entitiesAtTile = this.level.tileManager.at(x, y);
    return entitiesAtTile.find(entity => entity.owner?.has(TargetableComponent));
  }

  private tileToWorldPos(tileX: number, tileY: number) {
    return vec(
      tileX * TileSize + TileHalfSize,
      tileY * TileSize + TileHalfSize,
    );
  }

  override async Execute() {
    const eventSubscriptions = [
      this.level.pathmap.on(
        "pointerup",
        toTileEvent(this.level.pathmap, this.mapClicked.bind(this)),
      ),
      this.level.turnBasedSystem.events.on("movecomplete", () => {
        if (this.hoverTile) this.updatePreview(this.hoverTile, true);
      }),
      this.level.engine.input.keyboard.on("release", (evt: KeyEvent) => {
        if (this.level.turnBasedSystem.updating) return;
        // Only handle ESC if we're the active flow
        if (!this.active) return;
        if (evt.key == Keys.Esc) this.Cancel();
      }),
    ];

    // Only add pointermove listener on desktop - mobile uses tap confirmation
    if (!isMobile()) {
      eventSubscriptions.push(
        this.level.pathmap.on(
          "pointermove",
          toTileEvent(this.level.pathmap, this.mapHover.bind(this)),
        ),
      );
    }

    this.closeOnComplete(...eventSubscriptions);

    // This shows a preview on start, but it's not great for mobile and shows over existin buildings
    // maybe we dont' need it.
    // if (this.level.engine.input.pointers.primary.lastScreenPos.magnitude > 0) {
    //   const hoverTile = this.level.pathmap.getTileByPoint(
    //     this.level.engine.input.pointers.primary.lastScreenPos,
    //   );
    //   if (hoverTile) this.updatePreview(hoverTile);
    // }
  }

  private clearGraphics() {
    this.towerPreview = undefined;
    this.towerUpgradeMask = undefined;
    this.confirmationOverlay = undefined;

    // Clear sticky hover target when cancelling/completing placement flow
    if (this.level.targetManager) {
      this.level.targetManager.clearStickyHover();
    }
  }

  set towerUpgradeMask(mask: Actor | undefined) {
    if (this._towerUpgradeMask) {
      this._towerUpgradeMask.kill();
    }
    this._towerUpgradeMask = mask;
    if (mask) this.level.add(mask);
  }

  set towerPreview(preview: Actor | undefined) {
    if (this._towerPreview) {
      this._towerPreview.kill();
    }
    this._towerPreview = preview;
    if (preview) this.level.add(preview);
  }

  set confirmationOverlay(overlay: Actor | undefined) {
    if (this._confirmationOverlay) {
      this._confirmationOverlay.kill();
    }
    this._confirmationOverlay = overlay;
    if (overlay) this.level.add(overlay);
  }

  Complete(result: PlaceBuildingResult) {
    this.clearGraphics(); // Wait until after we drop the event listeners
    super.Complete(result);
    this.clearGraphics(); // Wait until after we drop the event listeners
  }

  hoverTile: Tile | undefined;
  mapHover(evt: TileEvent) {
    this.updatePreview(evt.tile);
  }

  private updatePreview(tile: Tile, force: boolean = false) {
    if (this.hoverTile && tile == this.hoverTile && !force) {
      return;
    }
    this.clearGraphics();

    if (!tile) {
      this.hoverTile = undefined;
      return;
    }

    this.hoverTile = tile;

    const worldPos = this.tileToWorldPos(tile.x, tile.y);
    const canPlace = this.canPlace(tile.x, tile.y);

    if (canPlace) {
      const { matchingNeighbors, buildError, next, buildChain } =
        findBuildableOption(
          this.playable,
          vec(tile.x, tile.y),
          this.getNeighborsAt.bind(this),
          this.canBuild.bind(this),
        );
      if (buildError) {
        console.log(buildError);
        this.towerUpgradeMask = this.createErrorMask(matchingNeighbors);

        // Create ErrorPreviewBuilding to show missing resources
        const errorPreviewBuilding = new ErrorPreviewBuilding({
          tilePos: vec(tile.x, tile.y),
          playable: buildError.playable,
          buildError,
          buildChain,
          mapCols: this.level.tileManager.cols,
        });

        // Position at world coordinates
        errorPreviewBuilding.pos = worldPos;
        this.towerPreview = errorPreviewBuilding;
      } else {
        this.towerUpgradeMask = this.createUpgradeMask(matchingNeighbors);

        // Create PreviewBuilding for resource delta display
        const resourceDelta = this.calculateCombinationDelta(
          next,
          matchingNeighbors,
        );
        const previewBuilding = new PreviewBuilding({
          tilePos: vec(tile.x, tile.y),
          playable: next,
          resourceDelta,
          buildChain,
        });

        // On mobile, preview building should handle placement directly
        if (isMobile()) {
          previewBuilding.on("pointerup", () => {
            this.placeBuilding(tile.x, tile.y);
          });
        } else {
          // On desktop, use the existing tile event system
          previewBuilding.on(
            "pointerup",
            toTileEvent(this.level.pathmap, this.mapClicked.bind(this)),
          );
        }

        // Position at world coordinates
        previewBuilding.pos = worldPos;
        this.towerPreview = previewBuilding;
      }
    } else {
      this.towerPreview = undefined;
    }
  }

  private canBuild(
    playable: Playable,
    matchingNeighbors: Entity<CombinableComponent>[] = [],
  ):
    | {
        ok: false;
        error: BuildError;
      }
    | { ok: true; error: undefined } {
    const missingUnlocks: import("../types").ResourceKind[] = [];
    const missingResources: {
      resource: import("../types").GameResource;
      required: number;
      available: number;
    }[] = [];
    // Check unlock requirements
    for (const requiredUnlock of playable.requiredUnlock) {
      if (!this.level.gameState.seenResources.has(requiredUnlock)) {
        missingUnlocks.push(requiredUnlock);
      }
    }

    // Check resource requirements using net delta (accounts for buildings being replaced)
    const resourceDelta = this.calculateCombinationDelta(
      playable,
      matchingNeighbors,
    );
    for (const resource of GameResources) {
      const netChange = resourceDelta[resource];
      if (netChange < 0) {
        // Negative means this building consumes resources
        const required = Math.abs(netChange);
        const available = this.level.dynamicResources[resource];
        if (available < required) {
          missingResources.push({
            resource,
            required,
            available,
          });
        }
      }
    }

    // If there are any missing requirements, return error with details
    if (missingUnlocks.length > 0 || missingResources.length > 0) {
      return {
        ok: false,
        error: {
          playable,
          missingUnlocks,
          missingResources,
        },
      };
    }

    return { ok: true, error: undefined };
  }

  private createUpgradeMask(
    matches: Entity<CombinableComponent>[],
  ): Actor | undefined {
    if (matches.length === 0) return undefined;

    const maskPositions = matches
      .map((m) => m.get(TransformComponent)?.pos)
      .filter(notEmpty)
      .map((pos) => ({ x: pos.x, y: pos.y }));

    if (maskPositions.length === 0) return undefined;

    const bounds = this.calculateBounds(maskPositions);
    const upgradeMask = new Actor({
      name: actorName("upgradeMask"),
      ...bounds.topLeft,
      width: bounds.width,
      height: bounds.height,
      z: PreviewZIndex,
    });

    maskPositions.forEach(({ x, y }) => {
      const childOverlay = new Actor({
        x: x - bounds.topLeft.x,
        y: y - bounds.topLeft.y,
        width: TileSize,
        height: TileSize,
      });
      childOverlay.graphics.use(upgradeOverlay);
      upgradeMask.addChild(childOverlay);
    });

    return upgradeMask;
  }

  private createErrorMask(
    matches: Entity<CombinableComponent>[],
  ): Actor | undefined {
    if (matches.length === 0) return undefined;

    const maskPositions = matches
      .map((m) => m.get(TransformComponent)?.pos)
      .filter(notEmpty)
      .map((pos) => ({ x: pos.x, y: pos.y }));

    if (maskPositions.length === 0) return undefined;

    const bounds = this.calculateBounds(maskPositions);
    const errorMask = new Actor({
      name: actorName("errorMask"),
      ...bounds.topLeft,
      width: bounds.width,
      height: bounds.height,
      z: PreviewZIndex,
    });

    maskPositions.forEach(({ x, y }) => {
      const childOverlay = new Actor({
        x: x - bounds.topLeft.x,
        y: y - bounds.topLeft.y,
        width: TileSize,
        height: TileSize,
      });
      childOverlay.graphics.use(errorOverlay);
      errorMask.addChild(childOverlay);
    });

    return errorMask;
  }

  private calculateBounds(positions: { x: number; y: number }[]) {
    const xs = positions.map((p) => p.x);
    const ys = positions.map((p) => p.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const maxX = Math.max(...xs);
    const maxY = Math.max(...ys);

    return {
      topLeft: { x: minX, y: minY },
      width: maxX - minX,
      height: maxY - minY,
    };
  }

  private mapClicked(evt: TileEvent) {
    const { x, y } = evt.tile;

    // Handle mobile confirmation mode
    if (isMobile()) {
      return this.handleMobileClick(evt.tile);
    }

    // Desktop behavior - immediate placement
    this.placeBuilding(x, y);
  }

  private handleMobileClick(tile: Tile) {
    const { x, y } = tile;

    // Check if there's an existing building on this tile
    const buildingOnTile = this.getBuildingAtTile(x, y);
    if (buildingOnTile?.owner) {
      // Don't intercept - let building handle its own pointerdown for hover toggle
      // But clear any active placement confirmation
      this.clearConfirmation();
      return;
    }

    // If clicking elsewhere, clear confirmation and set new one (if valid)
    if (this.canPlace(x, y)) {
      this._confirmationTile = tile;
      this.updatePreview(tile, true);
      this.showConfirmationOverlay(x, y);

      // For empty tiles, use the preview building we just created as hover target
      if (this._towerPreview && this.level.targetManager) {
        this.level.targetManager.toggleStickyHover(this._towerPreview);
      }
    } else {
      this.clearConfirmation();
    }
  }

  private showConfirmationOverlay(x: number, y: number) {
    const worldPos = this.tileToWorldPos(x, y);
    const overlay = new Actor({
      name: actorName("confirmationOverlay"),
      x: worldPos.x,
      y: worldPos.y,
      width: TileSize,
      height: TileSize,
      z: PreviewZIndex - 1,
    });
    overlay.graphics.use(confirmationOverlay);
    this.confirmationOverlay = overlay;
  }

  private clearConfirmation() {
    this._confirmationTile = undefined;
    this.confirmationOverlay = undefined;

    // Clear the preview building (this removes the building image)
    this.towerPreview = undefined;
    this.towerUpgradeMask = undefined;

    // Clear sticky hover target to hide tile hover overlay
    if (this.level.targetManager) {
      this.level.targetManager.clearStickyHover();
    }
  }

  private placeBuilding(x: number, y: number) {
    if (!this.canPlace(x, y)) return;

    const pos = vec(x, y);
    const { matchingNeighbors, buildError, next } = findBuildableOption(
      this.playable,
      pos,
      this.getNeighborsAt.bind(this),
      this.canBuild.bind(this),
    );

    if (buildError) {
      console.log(buildError);

      const worldPos = this.tileToWorldPos(x, y);

      // Create ErrorPreviewBuilding to show missing resources
      const errorPreviewBuilding = new ErrorPreviewBuilding({
        tilePos: vec(x, y),
        playable: buildError.playable,
        buildError,
        buildChain: [],
        mapCols: this.level.tileManager.cols,
      });

      // Position at world coordinates
      errorPreviewBuilding.pos = worldPos;
      this.towerPreview = errorPreviewBuilding;

      // Clear after a delay
      setTimeout(() => {
        if (this._towerPreview === errorPreviewBuilding) {
          this.towerPreview = undefined;
        }
      }, 2000);

      return;
    }

    matchingNeighbors.forEach((b) => b.get(CombinableComponent).combine());

    this.level.add(
      new Building({
        playable: next,
        tilePos: pos,
      }),
    );

    // Clear sticky hover on successful placement
    if (this.level.targetManager) {
      this.level.targetManager.clearStickyHover();
    }

    this.Complete({
      pos,
      playableId: next.name,
      placed: true,
      combined: matchingNeighbors.length > 0,
    });
  }
}
